<template>
  <div class="tool-page">
    <div class="tool-header">
      <h1>{{ $t('wordcount.title') }}</h1>
      <p>{{ $t('wordcount.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="stats-grid">
        <div class="stat-item">
          <div class="stat-value">{{ stats.characters }}</div>
          <div class="stat-label">{{ $t('wordcount.totalChars') }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ stats.charactersNoSpace }}</div>
          <div class="stat-label">{{ $t('wordcount.charsNoSpace') }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ stats.chinese }}</div>
          <div class="stat-label">{{ $t('wordcount.chineseChars') }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ stats.english }}</div>
          <div class="stat-label">{{ $t('wordcount.englishChars') }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ stats.words }}</div>
          <div class="stat-label">{{ $t('wordcount.englishWords') }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ stats.numbers }}</div>
          <div class="stat-label">{{ $t('wordcount.numbers') }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ stats.lines }}</div>
          <div class="stat-label">{{ $t('wordcount.lines') }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ stats.paragraphs }}</div>
          <div class="stat-label">{{ $t('wordcount.paragraphs') }}</div>
        </div>
      </div>

      <div class="editor-section">
        <div class="editor-header">
          <label>{{ $t('wordcount.inputLabel') }}</label>
          <button class="btn small" @click="clearText">{{ $t('common.clear') }}</button>
        </div>
        <textarea
          v-model="text"
          :placeholder="$t('wordcount.inputPlaceholder')"
          @input="updateStats"
        ></textarea>
      </div>

      <div class="tip">
        {{ $t('wordcount.tip') }}
      </div>
    </div>

    <!-- 工具说明 -->
    <div class="tool-description">
      <h2>{{ $t('wordcount.whyWordCount') }}</h2>
      <p>{{ $t('wordcount.whyWordCountDesc') }}</p>

      <h2>{{ $t('wordcount.wordCountUseCases') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('wordcount.wordCountUseCasesItems')" :key="'use'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('wordcount.countingRules') }}</h2>
      <p>{{ $t('wordcount.countingRulesDesc') }}</p>
      <ul>
        <li v-for="(item, index) in $t('wordcount.countingRulesItems')" :key="'rule'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('wordcount.writingTips') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('wordcount.writingTipsItems')" :key="'tip'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('wordcount.platformLimits') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('wordcount.platformLimitsItems')" :key="'plat'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('wordcount.readingTime') }}</h2>
      <p>{{ $t('wordcount.readingTimeDesc') }}</p>

      <h2>{{ $t('wordcount.seoWordCount') }}</h2>
      <p>{{ $t('wordcount.seoWordCountDesc') }}</p>

      <h2>{{ $t('wordcount.writingEfficiency') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('wordcount.writingEfficiencyItems')" :key="'eff'+index">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WordCount',
  data() {
    return {
      text: '',
      stats: {
        characters: 0,
        charactersNoSpace: 0,
        chinese: 0,
        english: 0,
        words: 0,
        numbers: 0,
        lines: 0,
        paragraphs: 0
      }
    }
  },
  methods: {
    updateStats() {
      const text = this.text

      // 总字符数
      this.stats.characters = text.length

      // 不含空格的字符数
      this.stats.charactersNoSpace = text.replace(/\s/g, '').length

      // 中文字符数 (包括中文标点)
      const chineseMatch = text.match(/[\u4e00-\u9fa5]/g)
      this.stats.chinese = chineseMatch ? chineseMatch.length : 0

      // 英文字母数
      const englishMatch = text.match(/[a-zA-Z]/g)
      this.stats.english = englishMatch ? englishMatch.length : 0

      // 英文单词数
      const wordsMatch = text.match(/[a-zA-Z]+/g)
      this.stats.words = wordsMatch ? wordsMatch.length : 0

      // 数字个数
      const numbersMatch = text.match(/\d/g)
      this.stats.numbers = numbersMatch ? numbersMatch.length : 0

      // 行数
      if (text.length === 0) {
        this.stats.lines = 0
      } else {
        this.stats.lines = text.split('\n').length
      }

      // 段落数 (非空行)
      const paragraphs = text.split('\n').filter(line => line.trim().length > 0)
      this.stats.paragraphs = paragraphs.length
    },

    clearText() {
      this.text = ''
      this.updateStats()
    }
  },
  mounted() {
    this.updateStats()
  }
}
</script>

<style scoped>
.tool-page {
  max-width: 900px;
  margin: 0 auto;
}

.tool-header {
  text-align: center;
  margin-bottom: 32px;
}

.tool-header h1 {
  font-size: 32px;
  margin-bottom: 12px;
  color: var(--text-primary);
}

.tool-header p {
  color: var(--text-secondary);
  font-size: 16px;
}

.tool-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 24px;
  box-shadow: var(--shadow-md);
  transition: background 0.3s, border-color 0.3s;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.stat-item {
  background: var(--accent-light);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  transition: all 0.2s;
}

.stat-item:hover {
  border-color: var(--accent-color);
  transform: translateY(-2px);
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: var(--accent-color);
  font-family: 'Monaco', 'Menlo', monospace;
}

.stat-label {
  font-size: 13px;
  color: var(--text-secondary);
  margin-top: 4px;
}

.editor-section {
  margin-bottom: 16px;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.editor-header label {
  font-size: 14px;
  color: var(--text-secondary);
}

.btn {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn:hover {
  background: var(--bg-hover);
}

.btn.small {
  padding: 6px 12px;
  font-size: 12px;
}

textarea {
  width: 100%;
  min-height: 300px;
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
  color: var(--text-primary);
  font-size: 15px;
  line-height: 1.6;
  resize: vertical;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  transition: background 0.3s, border-color 0.3s, color 0.3s;
}

textarea:focus {
  outline: none;
  border-color: var(--accent-color);
}

textarea::placeholder {
  color: var(--text-muted);
}

.tip {
  font-size: 13px;
  color: var(--text-muted);
  text-align: center;
}

.tool-description {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 32px;
  margin-top: 32px;
  box-shadow: var(--shadow-md);
}

.tool-description h2 {
  font-size: 20px;
  color: var(--text-primary);
  margin: 28px 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.tool-description h2:first-child {
  margin-top: 0;
}

.tool-description p {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: 12px;
}

.tool-description ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tool-description li {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  padding-left: 20px;
  position: relative;
  margin-bottom: 8px;
}

.tool-description li::before {
  content: '•';
  color: var(--accent-color);
  position: absolute;
  left: 0;
}
</style>
